import { useEffect, useState } from "react";
import { Star, Quote } from "lucide-react";
import { fetchPublicTestimonials } from "@/lib/api";

/**
 * Renders a responsive grid of approved testimonials on the public About
 * page. Fetches `/api/testimonials/public` on mount; self-hides if the
 * feed is empty so brand-new deployments don't show an awkward void.
 *
 * Each card shows the submitter's name + garden location + tier badge,
 * avatar (fallback to initial-in-circle), star rating, and the full quote.
 */

function TierBadge({ tier }) {
    const map = {
        plus: { label: "Plus", className: "text-[var(--vm-accent)] border-[var(--vm-accent)]/40 bg-[var(--vm-accent)]/10" },
        community: { label: "Community", className: "text-[var(--vm-sage)] border-[var(--vm-sage)]/40 bg-[var(--vm-sage)]/10" },
        free: { label: "Free", className: "text-[var(--vm-text-muted)] border-[var(--vm-border)] bg-[var(--vm-card)]/80" },
    };
    const m = map[tier] || map.free;
    return (
        <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[9px] uppercase tracking-wide font-semibold ${m.className}`}
            data-testid={`testimonial-tier-${tier}`}
        >
            {m.label}
        </span>
    );
}

function Avatar({ name, url }) {
    const initial = (name || "?").trim().charAt(0).toUpperCase();
    if (url) {
        return (
            <img
                src={url}
                alt={`${name || "Gardener"}'s avatar`}
                loading="lazy"
                className="w-11 h-11 rounded-full object-cover border border-[var(--vm-border)]"
            />
        );
    }
    return (
        <div className="w-11 h-11 rounded-full bg-[var(--vm-accent)]/18 border border-[var(--vm-accent)]/30 flex items-center justify-center font-serif-display text-xl text-[var(--vm-accent)]">
            {initial}
        </div>
    );
}

function Rating({ n }) {
    if (!n) return null;
    return (
        <div className="flex items-center gap-0.5" aria-label={`${n} out of 5 stars`}>
            {[1, 2, 3, 4, 5].map((i) => (
                <Star
                    key={i}
                    className={`w-3.5 h-3.5 ${i <= n ? "fill-[var(--vm-accent)] text-[var(--vm-accent)]" : "text-[var(--vm-border)]"}`}
                    strokeWidth={1.5}
                />
            ))}
        </div>
    );
}

export default function TestimonialsSection() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let ignore = false;
        fetchPublicTestimonials(9)
            .then((rows) => { if (!ignore) setItems(rows || []); })
            .catch(() => { if (!ignore) setItems([]); })
            .finally(() => { if (!ignore) setLoading(false); });
        return () => { ignore = true; };
    }, []);

    // Auto-hide while empty so a brand-new deployment doesn't render an
    // empty section. The `loading` skeleton is hidden entirely — first
    // paint for unauthenticated visitors should never flash a loading row.
    if (loading || items.length === 0) return null;

    return (
        <section
            className="max-w-6xl mx-auto px-6 py-20"
            data-testid="about-testimonials"
        >
            <div className="max-w-2xl mb-10">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-[var(--vm-border)] bg-[var(--vm-card)]/60 text-[10px] uppercase tracking-[0.18em] text-[var(--vm-sage)]">
                    From our gardeners
                </span>
                <h2 className="font-serif-display text-4xl lg:text-5xl text-[var(--vm-text)] mt-4">
                    A journal, not a spreadsheet.
                </h2>
                <p className="text-base text-[var(--vm-text-secondary)] mt-4 leading-relaxed">
                    Honest notes from real home gardeners growing with rooteed this season.
                </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {items.map((t) => (
                    <article
                        key={t.id}
                        data-testid={`testimonial-card-${t.id}`}
                        className="journal-card p-6 flex flex-col gap-4"
                    >
                        <Quote className="w-5 h-5 text-[var(--vm-accent)]/50 shrink-0" strokeWidth={1.5} />
                        <p className="text-sm text-[var(--vm-text)] leading-relaxed italic flex-1">
                            "{t.quote}"
                        </p>
                        <div className="flex items-center gap-3 pt-2 border-t border-[var(--vm-border)]/60">
                            <Avatar name={t.name} url={t.avatar_url} />
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-medium text-[var(--vm-text)] text-sm truncate">{t.name}</span>
                                    <TierBadge tier={t.tier} />
                                </div>
                                {t.location && (
                                    <div className="text-xs text-[var(--vm-text-muted)] mt-0.5 truncate">
                                        {t.location}
                                    </div>
                                )}
                                {t.rating ? <div className="mt-1"><Rating n={t.rating} /></div> : null}
                            </div>
                        </div>
                    </article>
                ))}
            </div>
        </section>
    );
}
